import { forwardRef, useEffect, useReducer, useState, ClipboardEvent } from 'react'
import * as React from 'react'
import cx from 'classnames'
import { partition } from 'lodash'
import Foco from 'react-foco'

import Styles from './SharedRecipientSelector.module.scss'
import { Suggestion, UserValue, Value, PsuedoGroupValue } from 'components/ShareSheet/RecipientSelector/Types'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { HTMLInputEvent, RecipientsSelectorValueType } from 'types'
import { isEqual, take } from 'lodash'
import { RecipientBadge } from 'components/ShareSheet/RecipientBadge/RecipientBadge'
import { RecipientSuggestion } from './RecipientSuggestion'
import { isEmail } from 'validator'
import RelativePortal from 'react-relative-portal'
import { RecipientsSelectorProps, GroupInfo } from './Types'
import { DefaultGroupRow } from './DefaultGroupRow'
import { RecipientBadgeProps } from 'components/ShareSheet/RecipientBadge/Types'
import { parseEmails } from 'utils'
import { reducer, filterSelectedMatches, buildGroupValue } from './utils'

export const SharedRecipientSelector = forwardRef(
  (
    {
      id,
      sourceData,
      recipients,
      onChange,
      placeholder = 'Select recipients',
      className,
      suggestionsLimit = 5,
      limit,
      onFocus,
      onBlur,
      topGroups = [],
      allowEmails = true,
      disabled = false,
      onRecipientActionClick,
      recipientsClassNames,
      dropdownClassName,
      reviewerRolesDropdown,
    }: RecipientsSelectorProps,
    forwardedRef: any,
  ) => {
    const [state, dispatch] = useReducer(reducer, {
      query: '',
      matches: [],
      sourceData,
      highlightedSuggestionIndex: -1,
      highlightedRecipientIndex: -1,
    })
    const [showSuggestions, setShowSuggestions] = useState(false)
    const { highlightedSuggestionIndex, highlightedRecipientIndex, matches, query } = state
    const suggestions = filterSelectedMatches(matches, recipients)
    const currentWorkspace = useCurrentWorkspace()
    const [groupSuggestions, userSuggestions] = partition(
      suggestions,
      ({ type }) => type === RecipientsSelectorValueType.Channel,
    )
    const sortedSuggestions = [
      ...topGroups.map((data) => {
        const { group } = data

        if (group.type === RecipientsSelectorValueType.LayerEditors)
          return { type: data.group.type, value: data.group } as PsuedoGroupValue

        return { type: data.group.type, value: data.group } as PsuedoGroupValue
      }),
      ...take(userSuggestions, suggestionsLimit),
      ...take(groupSuggestions, suggestionsLimit),
    ]
    const activeSuggestion = highlightedSuggestionIndex > -1 ? sortedSuggestions[highlightedSuggestionIndex] : null
    const hitLimit = limit ? recipients.length === limit : false

    useEffect(() => {
      dispatch({ type: 'updateSourceData', sourceData: sourceData })
    }, [JSON.stringify(sourceData)]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleInputChange = (e: HTMLInputEvent) => {
      if (highlightedRecipientIndex !== 'all' && highlightedRecipientIndex > -1) {
        // Disabling typing if selecting a recipient
        return
      }

      dispatch({ type: 'updateQuery', query: e.target.value })
    }

    const highlightSuggestion = (direction: 'prev' | 'next') => {
      const { highlightedSuggestionIndex } = state
      let nextIndex = highlightedSuggestionIndex
      const wrapAround = sortedSuggestions.length - 1

      if (direction === 'next') {
        if (highlightedSuggestionIndex >= wrapAround) {
          nextIndex = 0
        } else {
          nextIndex += 1
        }
      } else {
        if (highlightedSuggestionIndex <= 0) {
          nextIndex = sortedSuggestions.length - 1
        } else {
          nextIndex -= 1
        }
      }
      dispatch({ type: 'updateHighlightedSuggestionIndex', index: nextIndex })
    }

    const highlightRecipient = (direction: 'prev' | 'next') => {
      if (state.query) {
        return
      }

      let next = state.highlightedRecipientIndex

      if (direction === 'next') {
        if (state.highlightedRecipientIndex === 'all') {
          next = recipients.length ? 0 : -1
        } else if (recipients.length - 1 > state.highlightedRecipientIndex) {
          ;(next as number) += 1
        } else {
          next = -1
        }
      } else {
        if (recipients.length <= 0) {
          next = 'all'
        } else if (state.highlightedRecipientIndex === -1) {
          // hitting left when the being in the input selects the latest one.
          next = recipients.length - 1
        } else if (state.highlightedRecipientIndex !== 'all' && 0 < state.highlightedRecipientIndex) {
          ;(next as number) -= 1
        } else if (state.highlightedRecipientIndex === 0) {
          next = 'all'
        }
      }

      dispatch({ type: 'updateHighlightedRecipientIndex', index: next })
    }

    const addRecipient = (e?: React.SyntheticEvent) => {
      if (activeSuggestion) {
        dispatch({ type: 'updateQuery', query: '' })
        const newRecipient = ['layerEditors', 'everyoneAtCompany'].includes(activeSuggestion.type)
          ? buildGroupValue(activeSuggestion.type, activeSuggestion.value as GroupInfo, currentWorkspace)
          : activeSuggestion
        onChange([...recipients, newRecipient])
        e?.preventDefault()
        return
      }

      if (allowEmails && query !== '' && isEmail(query)) {
        e?.preventDefault()

        const matchingUser = getMatchingUser(query)

        if (matchingUser) {
          if (isAlreadyInRecipients(query)) {
            return
          } else {
            dispatch({ type: 'updateQuery', query: '' })
            onChange([...recipients, matchingUser])
            e?.preventDefault()
            return
          }
        }

        dispatch({ type: 'updateQuery', query: '' })
        onChange([...recipients, { type: RecipientsSelectorValueType.Email, value: query }])
      }
    }

    const getMatchingUser = (email: string): Suggestion | undefined => {
      return matches.find(
        ({ type, value }) => type === RecipientsSelectorValueType.User && (value as UserValue['value']).email === email,
      )
    }

    const isAlreadyInRecipients = (email: string): boolean => {
      return !!recipients.find(
        (recipient) => recipient.type === RecipientsSelectorValueType.User && recipient.value.email === email,
      )
    }

    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault()

      if (!e.clipboardData.getData('Text')) return

      const canAddEmail = (email: string): boolean => {
        return allowEmails && !isAlreadyInRecipients(email)
      }

      const [parsedEmails] = parseEmails(e.clipboardData.getData('Text'))
      const validEmails: Value[] = parsedEmails
        .filter((email) => canAddEmail(email))
        .map((email) => getMatchingUser(email) || { type: RecipientsSelectorValueType.Email, value: email })

      onChange([...recipients, ...validEmails])
    }

    const handleBackspaceKey = () => {
      if (highlightedRecipientIndex !== -1) {
        if (highlightedRecipientIndex !== 'all') {
          removeByIndex(highlightedRecipientIndex)
          dispatch({ type: 'updateHighlightedRecipientIndex', index: (highlightedRecipientIndex as number) - 1 })
        }

        return
      }

      if (query === '') {
        onChange(recipients.slice(0, -1))
      }
    }

    const handleTabKey = (e: React.KeyboardEvent) => {
      if (highlightedRecipientIndex !== -1) {
        e.preventDefault()
        dispatch({ type: 'updateHighlightedRecipientIndex', index: -1 })
        return
      }

      addRecipient(e)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          if (!state.query) {
            e.preventDefault()
            highlightRecipient('prev')
          }
          break
        case 'ArrowRight':
          if (!state.query) {
            e.preventDefault()
            highlightRecipient('next')
          }
          break
        case 'ArrowDown':
          e.preventDefault()
          highlightSuggestion('next')
          break
        case 'ArrowUp':
          e.preventDefault()
          highlightSuggestion('prev')
          break
        case ',':
        case 'Enter':
        case ' ':
        case ';':
          addRecipient(e)
          break
        case 'Tab': {
          handleTabKey(e)
          break
        }
        case 'Backspace':
          handleBackspaceKey()
          break
      }
    }

    const handleFocus = () => {
      dispatch({ type: 'updateHighlightedSuggestionIndex', index: -1 })
      setShowSuggestions(true)

      onFocus?.()
    }

    const handleBlur = () => {
      onBlur?.()
    }

    const handleClose = () => {
      dispatch({ type: 'updateQuery', query: '' })
      setShowSuggestions(false)
    }

    const onClickOutside = () => {
      addRecipient()
      handleClose()
    }

    const removeByIndex = (index: number) => {
      const newRecipients = [...recipients]
      newRecipients.splice(index, 1)
      onChange(newRecipients)
    }

    const renderSuggestion = (recipient: Suggestion, index: number) => {
      const highlighted = isEqual(index, highlightedSuggestionIndex)
      const id =
        recipient.type === RecipientsSelectorValueType.AccessMemberGroup ? recipient.value.label : recipient.value.id
      const key = `${recipient.type}-${id}`
      const onMouseEnter = () => dispatch({ type: 'updateHighlightedSuggestionIndex', index })
      const onMouseLeave = () => dispatch({ type: 'updateHighlightedSuggestionIndex', index: -1 })
      const onMouseDown = (e: React.MouseEvent) => {
        e.preventDefault()
        dispatch({ type: 'updateQuery', query: '' })
        onChange([...recipients, recipient])
      }

      return (
        <RecipientSuggestion
          key={key}
          recipient={recipient}
          highlighted={highlighted}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onMouseDown={onMouseDown}
        />
      )
    }

    const renderSuggestions = () => {
      if (sortedSuggestions.length === 0 || hitLimit) {
        return null
      }

      return (
        <RelativePortal component="div" className={Styles.portal} fullWidth left={0} right={0} top={0}>
          <div className={Styles.suggestionsPanel}>
            {topGroups.map((groupData, index) => (
              <DefaultGroupRow
                key={`default-group-${groupData.group.id}-${index}`}
                group={groupData.group}
                childRows={groupData.childRows}
                dispatch={dispatch}
                index={index}
                activeIndex={highlightedSuggestionIndex}
                refocus={() => forwardedRef?.current?.focus()}
                addGroup={(value: Value) => {
                  onChange([...recipients, value])
                }}
              />
            ))}

            {!!userSuggestions.length && <div className={Styles.searchHeader}>Members</div>}
            {take(userSuggestions, suggestionsLimit).map((suggestion, index) =>
              renderSuggestion(suggestion, index + topGroups.length),
            )}
            {!!groupSuggestions.length && <div className={Styles.searchHeader}>Groups</div>}
            {take(groupSuggestions, suggestionsLimit).map((suggestion, index) => {
              return renderSuggestion(suggestion, index + topGroups.length + Math.min(userSuggestions.length, 5))
            })}
          </div>
        </RelativePortal>
      )
    }

    return (
      <Foco className={cx(Styles.sharedContainer, dropdownClassName)} onClickOutside={onClickOutside}>
        <div className={className}>
          <div className={cx(Styles.root)}>
            <div className={cx(Styles.recipients, recipientsClassNames)}>
              {recipients.map((recipient, index) => {
                const props: RecipientBadgeProps = {
                  recipient,
                  onRemove: () => removeByIndex(index),
                  className: cx({ [Styles.badgeSelected]: index === highlightedRecipientIndex }),
                  onClick: () => dispatch({ type: 'updateHighlightedRecipientIndex', index }),
                  onActionClick: onRecipientActionClick,
                }
                return <RecipientBadge key={`${recipient.type}-${index}`} {...props} />
              })}
            </div>
          </div>
          {!hitLimit && (
            <>
              <input
                aria-label="Recipients"
                id={id}
                ref={forwardedRef}
                value={state.query}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={recipients.length === 0 ? placeholder : ''}
                autoComplete="off"
                disabled={disabled}
                data-testid={id ? `recipients-selector-input-${id}` : 'recipients-selector-input'}
                className={cx(Styles.input, { [Styles.hideCaret]: highlightedRecipientIndex !== -1 })}
              />
              {reviewerRolesDropdown}
            </>
          )}
          {showSuggestions && renderSuggestions()}
        </div>
      </Foco>
    )
  },
)
